import axios, { AxiosInstance } from 'axios';

// API Base URL - determine based on how the frontend is served
// When served from nginx (Docker, port 3004), use /api baseURL - nginx proxies and strips /api prefix
// When running React dev server (typically port 3003), connect directly to backend at localhost:8004
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (window.location.port === '3003' ? 'http://localhost:8004' : '/api');

console.log('[API] Base URL:', API_BASE_URL || '(empty - using relative paths)', 'Port:', window.location.port);

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Build a fully-qualified URL for a receipt file/thumbnail path returned by the
// backend (e.g. "/receipts/files/xyz.jpg"). The backend serves these under the
// API base (/api in production), so a bare path would 404 against the SPA origin.
export const getReceiptUrl = (path?: string | null): string => {
  if (!path) return '';
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  const base = apiClient.defaults.baseURL || '';
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      config.headers['X-Refresh-Token'] = refreshToken;
    }

    const i18nextLng = localStorage.getItem('i18nextLng');
    if (i18nextLng) {
      config.headers['Accept-Language'] = i18nextLng;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Types
export interface User {
  id: number | string;
  username: string;
  email: string;
  created_at?: string;
  first_name?: string;
  last_name?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  phone?: string;
}

export interface UserProfileUpdate {
  first_name?: string;
  last_name?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  phone?: string;
}

export interface LoginCredentials {
  username: string;
  email: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface Client {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  client_id: string;
  user_id: string;
  name: string;
  description?: string;
  reporting_period_type?: string;
  reporting_period_config?: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  client_name?: string;
}

export type TimeEntryCategory = 'Admin' | 'Meetings' | 'Research' | 'Development' | string;

export interface Category {
  id: string;
  user_id: string;
  project_id: string;
  name: string;
  color: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoryCreate {
  name: string;
  color?: string;
}

export interface CategoryUpdate {
  name?: string;
  color?: string;
  is_active?: boolean;
}

export interface TimeEntry {
  id: string;
  project_id: string;
  user_id: string;
  date: string;
  description: string;
  hours: number;
  category: TimeEntryCategory;
  category_id?: string;
  subproject_id?: string | null;  // FK to project_subprojects
  subproject_name?: string | null;  // Resolved name from FK
  start_time?: string | null;  // Optional start time in HH:MM format (for meetings)
  start_time_tz?: string | null;  // IANA zone the start_time is expressed in
  attachment_count?: number;
  link_count?: number;
  created_at: string;
  updated_at: string;
  project_name?: string;
  client_name?: string;
  client_id?: string;
}

export interface Attachment {
  id: string;
  time_entry_id: string;
  user_id: string;
  original_filename: string;
  stored_filename: string;
  file_size: number;
  mime_type: string;
  ocr_text?: string | null;
  ocr_markdown?: string | null;
  ocr_status?: string | null;
  ocr_method?: string | null;
  ocr_model?: string | null;
  created_at: string;
  download_url: string;
}

export interface DailyNote {
  id: string;
  user_id: string;
  note_date: string;
  original_filename: string;
  stored_filename: string;
  file_size: number;
  mime_type: string;
  ocr_text?: string | null;
  ocr_markdown?: string | null;
  ocr_status?: string | null;
  ocr_method?: string | null;
  ocr_model?: string | null;
  created_at: string;
  download_url: string;
}

export interface OcrResult {
  ocr_text: string | null;
  ocr_markdown?: string | null;
  ocr_status: string | null;
  ocr_method?: string | null;
  ocr_model?: string | null;
}

export interface Link {
  id: string;
  time_entry_id: string;
  user_id: string;
  url: string;
  title?: string | null;
  created_at: string;
}

export interface LinkCreate {
  url: string;
  title?: string;
}

export interface EntryTemplate {
  id: string;
  user_id: string;
  name: string;
  project_id: string;
  category_id?: string;
  description?: string;
  default_hours?: number;
  is_favorite: boolean;
  start_time?: string;
  start_time_tz?: string | null;  // IANA zone this template's start_time is anchored to
  subproject_id?: string | null;
  subproject_name?: string | null;
  created_at: string;
  updated_at: string;
  project_name?: string;
  client_name?: string;
  client_id?: string;
  category_name?: string;
  category_color?: string;
}

export interface TemplateCreate {
  name: string;
  project_id: string;
  category_id?: string;
  description?: string;
  default_hours?: number;
  is_favorite?: boolean;
  start_time?: string;
  start_time_tz?: string | null;
  subproject_id?: string;
}

export interface TemplateUpdate {
  name?: string;
  project_id?: string;
  category_id?: string;
  description?: string;
  default_hours?: number;
  is_favorite?: boolean;
  start_time?: string;
  start_time_tz?: string | null;
  subproject_id?: string;
}

export interface TemplateApply {
  date: string;
  hours?: number;
  description?: string;
  start_time?: string;
  target_tz?: string;  // IANA zone the user is currently logging in
}

export interface BulkTimeEntryResult {
  created: TimeEntry[];
  errors: Array<{ index: number; error: string }>;
  total_created: number;
  total_errors: number;
}

// Create/Update types
export interface ClientCreate {
  name: string;
  description?: string;
}

export interface ClientUpdate {
  name?: string;
  description?: string;
  is_active?: boolean;
}

export interface ProjectCreate {
  name: string;
  client_id: string;
  description?: string;
  reporting_period_type?: string;
  reporting_period_config?: Record<string, any>;
}

export interface ProjectUpdate {
  name?: string;
  client_id?: string;
  description?: string;
  is_active?: boolean;
  reporting_period_type?: string;
  reporting_period_config?: Record<string, any>;
}

export interface TimeEntryCreate {
  project_id: string;
  date: string;
  description: string;
  hours: number;
  category: TimeEntryCategory;
  subproject_id?: string | null;  // FK to project_subprojects
  start_time?: string | null;  // Optional start time in HH:MM format (for meetings)
  start_time_tz?: string | null;  // IANA zone the start_time is expressed in
}

export interface TimeEntryUpdate {
  project_id?: string;
  date?: string;
  description?: string;
  hours?: number;
  category?: TimeEntryCategory;
  subproject_id?: string | null;  // FK to project_subprojects
  start_time?: string | null;  // Optional start time in HH:MM format (for meetings)
  start_time_tz?: string | null;  // IANA zone the start_time is expressed in
}

export interface DailySummary {
  date: string;
  total_hours: number;
  entry_count: number;
  entries: TimeEntry[];
}

export interface DateRangeSummary {
  start_date: string;
  end_date: string;
  total_hours: number;
  total_entries: number;
  by_client: Array<{
    client_id: string;
    client_name: string;
    total_hours: number;
    entry_count: number;
  }>;
  by_project: Array<{
    project_id: string;
    project_name: string;
    client_name: string;
    total_hours: number;
    entry_count: number;
  }>;
}

// Auth API
export const authAPI = {
  register: async (data: RegisterData): Promise<User> => {
    const response = await apiClient.post<User>('/auth/register', data);
    return response.data;
  },

  login: async (credentials: LoginCredentials): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/auth/login', credentials);
    return response.data;
  },

  refresh: async (refreshToken: string): Promise<TokenResponse> => {
    const response = await apiClient.post<TokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  logout: async (refreshToken: string): Promise<void> => {
    await apiClient.post('/auth/logout', { refresh_token: refreshToken });
  },

  verifyEmail: async (email: string, code: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/verify-email', {
      email,
      code,
    });
    return response.data;
  },

  resendVerificationEmail: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/resend-verification-email', {
      email,
    });
    return response.data;
  },

  resetPassword: async (email: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/reset-password', {
      email,
      new_password: newPassword,
    });
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/profile');
    return response.data;
  },

  updateProfile: async (data: UserProfileUpdate): Promise<User> => {
    const response = await apiClient.patch<User>('/auth/profile', data);
    return response.data;
  },
};

// Clients API
export const clientsAPI = {
  list: async (includeInactive: boolean = false): Promise<Client[]> => {
    const response = await apiClient.get<Client[]>('/clients', {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  get: async (id: string): Promise<Client> => {
    const response = await apiClient.get<Client>(`/clients/${id}`);
    return response.data;
  },

  create: async (data: { name: string; description?: string }): Promise<Client> => {
    const response = await apiClient.post<Client>('/clients', data);
    return response.data;
  },

  update: async (id: string, data: { name?: string; description?: string; is_active?: boolean }): Promise<Client> => {
    const response = await apiClient.put<Client>(`/clients/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/clients/${id}`);
  },
};

// Projects API
export const projectsAPI = {
  list: async (clientId?: string, includeInactive: boolean = false): Promise<Project[]> => {
    const response = await apiClient.get<Project[]>('/projects', {
      params: { client_id: clientId, include_inactive: includeInactive },
    });
    return response.data;
  },

  get: async (id: string): Promise<Project> => {
    const response = await apiClient.get<Project>(`/projects/${id}`);
    return response.data;
  },

  create: async (data: ProjectCreate): Promise<Project> => {
    const response = await apiClient.post<Project>('/projects', data);
    return response.data;
  },

  update: async (id: string, data: ProjectUpdate): Promise<Project> => {
    const response = await apiClient.put<Project>(`/projects/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/projects/${id}`);
  },
};

// ─── Subproject Types ───

export interface ProjectSubproject {
  id: string;
  project_id: string;
  user_id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectSubprojectCreate {
  project_id: string;
  name: string;
}

export interface ProjectSubprojectUpdate {
  name?: string;
  is_active?: boolean;
}

// Subprojects API
export const subprojectsAPI = {
  list: async (projectId: string, includeInactive: boolean = false): Promise<ProjectSubproject[]> => {
    const response = await apiClient.get<ProjectSubproject[]>(`/projects/${projectId}/subprojects`, {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  create: async (projectId: string, data: { name: string }): Promise<ProjectSubproject> => {
    const response = await apiClient.post<ProjectSubproject>(`/projects/${projectId}/subprojects`, data);
    return response.data;
  },

  update: async (projectId: string, subprojectId: string, data: ProjectSubprojectUpdate): Promise<ProjectSubproject> => {
    const response = await apiClient.put<ProjectSubproject>(`/projects/${projectId}/subprojects/${subprojectId}`, data);
    return response.data;
  },

  delete: async (projectId: string, subprojectId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}/subprojects/${subprojectId}`);
  },
};

// Categories API (project-scoped)
export const categoriesAPI = {
  list: async (projectId: string, includeInactive: boolean = false): Promise<Category[]> => {
    const response = await apiClient.get<Category[]>(`/projects/${projectId}/categories`, {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  create: async (projectId: string, data: CategoryCreate): Promise<Category> => {
    const response = await apiClient.post<Category>(`/projects/${projectId}/categories`, data);
    return response.data;
  },

  update: async (projectId: string, categoryId: string, data: CategoryUpdate): Promise<Category> => {
    const response = await apiClient.put<Category>(`/projects/${projectId}/categories/${categoryId}`, data);
    return response.data;
  },

  delete: async (projectId: string, categoryId: string): Promise<void> => {
    await apiClient.delete(`/projects/${projectId}/categories/${categoryId}`);
  },
};

// Time Entries API
export const timeEntriesAPI = {
  list: async (params?: { date?: string; start_date?: string; end_date?: string; project_id?: string; client_id?: string; search?: string; category?: string; subproject_id?: string }): Promise<TimeEntry[]> => {
    const response = await apiClient.get<TimeEntry[]>('/time-entries', { params });
    return response.data;
  },

  get: async (id: string): Promise<TimeEntry> => {
    const response = await apiClient.get<TimeEntry>(`/time-entries/${id}`);
    return response.data;
  },

  create: async (data: TimeEntryCreate): Promise<TimeEntry> => {
    const response = await apiClient.post<TimeEntry>('/time-entries', data);
    return response.data;
  },

  createBulk: async (entries: TimeEntryCreate[]): Promise<BulkTimeEntryResult> => {
    const response = await apiClient.post<BulkTimeEntryResult>('/time-entries/bulk', { entries });
    return response.data;
  },

  update: async (id: string, data: TimeEntryUpdate): Promise<TimeEntry> => {
    const response = await apiClient.put<TimeEntry>(`/time-entries/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/time-entries/${id}`);
  },

  getDailySummary: async (date: string): Promise<DailySummary> => {
    const response = await apiClient.get<DailySummary>(`/time-entries/daily/${date}`);
    return response.data;
  },

  getSummary: async (startDate: string, endDate: string): Promise<DateRangeSummary> => {
    const response = await apiClient.get<DateRangeSummary>('/time-entries/summary', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },
};

// ─── Attachments API ───

export const attachmentsAPI = {
  list: async (timeEntryId: string): Promise<Attachment[]> => {
    const response = await apiClient.get<Attachment[]>(
      `/time-entries/${timeEntryId}/attachments`
    );
    return response.data;
  },

  upload: async (timeEntryId: string, file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<Attachment>(
      `/time-entries/${timeEntryId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  download: async (attachmentId: string): Promise<Blob> => {
    const response = await apiClient.get(`/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  delete: async (attachmentId: string): Promise<void> => {
    await apiClient.delete(`/attachments/${attachmentId}`);
  },

  getOcr: async (attachmentId: string): Promise<OcrResult> => {
    const response = await apiClient.get(`/attachments/${attachmentId}/ocr`);
    return response.data;
  },

  retryOcr: async (attachmentId: string): Promise<OcrResult> => {
    const response = await apiClient.post(`/attachments/${attachmentId}/ocr`);
    return response.data;
  },

  saveCorrection: async (attachmentId: string, correctedMarkdown: string): Promise<OcrResult> => {
    const response = await apiClient.post(`/attachments/${attachmentId}/ocr/correction`, {
      corrected_markdown: correctedMarkdown,
    });
    return response.data;
  },
};

// ─── Daily Notes ("Other Notes") API ───

export const dailyNotesAPI = {
  list: async (noteDate: string): Promise<DailyNote[]> => {
    const response = await apiClient.get<DailyNote[]>(`/daily-notes/${noteDate}`);
    return response.data;
  },

  upload: async (noteDate: string, file: File): Promise<DailyNote> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<DailyNote>(
      `/daily-notes/${noteDate}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  download: async (noteId: string): Promise<Blob> => {
    const response = await apiClient.get(`/daily-note-files/${noteId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  delete: async (noteId: string): Promise<void> => {
    await apiClient.delete(`/daily-note-files/${noteId}`);
  },

  getOcr: async (noteId: string): Promise<OcrResult> => {
    const response = await apiClient.get(`/daily-note-files/${noteId}/ocr`);
    return response.data;
  },

  retryOcr: async (noteId: string): Promise<OcrResult> => {
    const response = await apiClient.post(`/daily-note-files/${noteId}/ocr`);
    return response.data;
  },

  saveCorrection: async (noteId: string, correctedMarkdown: string): Promise<OcrResult> => {
    const response = await apiClient.post(`/daily-note-files/${noteId}/ocr/correction`, {
      corrected_markdown: correctedMarkdown,
    });
    return response.data;
  },
};

// ─── Links API ───

export const linksAPI = {
  list: async (timeEntryId: string): Promise<Link[]> => {
    const response = await apiClient.get<Link[]>(
      `/time-entries/${timeEntryId}/links`
    );
    return response.data;
  },

  create: async (timeEntryId: string, data: LinkCreate): Promise<Link> => {
    const response = await apiClient.post<Link>(
      `/time-entries/${timeEntryId}/links`,
      data
    );
    return response.data;
  },

  delete: async (linkId: string): Promise<void> => {
    await apiClient.delete(`/links/${linkId}`);
  },
};

// Templates API
export const templatesAPI = {
  list: async (): Promise<EntryTemplate[]> => {
    const response = await apiClient.get<EntryTemplate[]>('/templates');
    return response.data;
  },

  get: async (id: string): Promise<EntryTemplate> => {
    const response = await apiClient.get<EntryTemplate>(`/templates/${id}`);
    return response.data;
  },

  create: async (data: TemplateCreate): Promise<EntryTemplate> => {
    const response = await apiClient.post<EntryTemplate>('/templates', data);
    return response.data;
  },

  update: async (id: string, data: TemplateUpdate): Promise<EntryTemplate> => {
    const response = await apiClient.put<EntryTemplate>(`/templates/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/templates/${id}`);
  },

  apply: async (id: string, data: TemplateApply): Promise<TimeEntry> => {
    const response = await apiClient.post<TimeEntry>(`/templates/${id}/apply`, data);
    return response.data;
  },
};

// ─── Expense Types ───

export interface ExpenseCategory {
  id: string;
  user_id: string;
  name: string;
  color: string;
  icon: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExpenseCategoryCreate {
  name: string;
  color?: string;
  icon?: string;
}

export interface ExpenseCategoryUpdate {
  name?: string;
  color?: string;
  icon?: string;
  is_active?: boolean;
}

export type PaymentMethod = 'credit_card' | 'debit_card' | 'cash' | 'check' | 'bank_transfer' | 'company_card' | 'personal_card' | 'other';

// ─── Business Trip Types ───

export type TripStatus = 'active' | 'completed' | 'submitted';

export interface BusinessTrip {
  id: string;
  user_id: string;
  project_id: string;
  subproject_id?: string | null;
  name: string;
  destination?: string;
  start_date: string;
  end_date: string;
  purpose?: string;
  status: TripStatus;
  created_at: string;
  updated_at: string;
  project_name: string;
  client_name: string;
  client_id: string;
  subproject_name?: string | null;
  expense_count: number;
  total_amount: number;
}

export interface TripCreate {
  project_id: string;
  subproject_id?: string | null;
  name: string;
  destination?: string;
  start_date: string;
  end_date: string;
  purpose?: string;
  status?: TripStatus;
}

export interface TripUpdate {
  project_id?: string;
  subproject_id?: string | null;
  name?: string;
  destination?: string;
  start_date?: string;
  end_date?: string;
  purpose?: string;
  status?: TripStatus;
}

export interface Expense {
  id: string;
  user_id: string;
  project_id: string;
  expense_category_id?: string;
  trip_id?: string;
  date: string;
  payee: string;
  amount: number;
  description?: string;
  notes?: string;
  receipt_image_url?: string;
  receipt_thumbnail_url?: string;
  ocr_data?: {
    raw_text: string;
    confidence: number;
    extracted_fields: Record<string, { value: any; confidence: number }>;
    processed_at: string;
  };
  is_reimbursable: boolean;
  payment_method: string;
  currency: string;
  created_at: string;
  updated_at: string;
  project_name?: string;
  client_name?: string;
  client_id?: string;
  category_name?: string;
  category_color?: string;
  category_icon?: string;
  trip_name?: string;
}

export interface ExpenseCreate {
  project_id: string;
  expense_category_id?: string;
  trip_id?: string;
  date: string;
  payee: string;
  amount: number;
  description?: string;
  notes?: string;
  is_reimbursable?: boolean;
  payment_method?: string;
  currency?: string;
}

export interface ExpenseUpdate {
  project_id?: string;
  expense_category_id?: string;
  trip_id?: string | null;
  date?: string;
  payee?: string;
  amount?: number;
  description?: string;
  notes?: string;
  receipt_image_url?: string;
  receipt_thumbnail_url?: string;
  ocr_data?: any;
  is_reimbursable?: boolean;
  payment_method?: string;
  currency?: string;
}

export interface BulkExpenseResult {
  created: Expense[];
  errors: Array<{ index: number; error: string }>;
  total_created: number;
  total_errors: number;
}

export interface ExpenseSummary {
  start_date: string;
  end_date: string;
  total_amount: number;
  total_expenses: number;
  by_client: Array<{ client_id: string; client_name: string; total_amount: number; entry_count: number }>;
  by_project: Array<{ project_id: string; project_name: string; client_name: string; total_amount: number; entry_count: number }>;
  by_category: Array<{ category_name: string; category_color: string; total_amount: number; entry_count: number }>;
}

export interface MileageEntry {
  id: string;
  expense_id: string;
  user_id: string;
  start_location: string;
  end_location: string;
  distance_miles: number;
  rate_per_mile: number;
  is_round_trip: boolean;
  created_at: string;
  updated_at: string;
  calculated_amount: number;
}

export interface MileageEntryCreate {
  project_id: string;
  date: string;
  start_location: string;
  end_location: string;
  distance_miles: number;
  rate_per_mile?: number;
  is_round_trip?: boolean;
  description?: string;
}

export interface PerDiemEntry {
  id: string;
  user_id: string;
  project_id: string;
  destination: string;
  start_date: string;
  end_date: string;
  meals_rate: number;
  lodging_rate: number;
  is_first_day_partial: boolean;
  is_last_day_partial: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  total_meals: number;
  total_lodging: number;
  total_amount: number;
  num_days: number;
}

export interface PerDiemEntryCreate {
  project_id: string;
  destination: string;
  start_date: string;
  end_date: string;
  meals_rate: number;
  lodging_rate: number;
  is_first_day_partial?: boolean;
  is_last_day_partial?: boolean;
  notes?: string;
}

export interface PerDiemRates {
  destinations: Record<string, { meals: number; lodging: number }>;
}

export interface ExpensePolicy {
  id: string;
  user_id: string;
  expense_category_id?: string;
  limit_per_transaction?: number;
  limit_daily?: number;
  limit_monthly?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  category_name?: string;
  category_color?: string;
  category_icon?: string;
}

export interface ExpensePolicyCreate {
  expense_category_id?: string;
  limit_per_transaction?: number;
  limit_daily?: number;
  limit_monthly?: number;
}

export interface ExpensePolicyUpdate {
  expense_category_id?: string;
  limit_per_transaction?: number;
  limit_daily?: number;
  limit_monthly?: number;
  is_active?: boolean;
}

export interface PolicyCheckResult {
  warnings: Array<{ type: string; category: string; limit: number; amount?: number; message: string }>;
  is_over_limit: boolean;
}

// ─── Expense Categories API ───

export const expenseCategoriesAPI = {
  list: async (includeInactive: boolean = false): Promise<ExpenseCategory[]> => {
    const response = await apiClient.get<ExpenseCategory[]>('/expense-categories', {
      params: { include_inactive: includeInactive },
    });
    return response.data;
  },

  create: async (data: ExpenseCategoryCreate): Promise<ExpenseCategory> => {
    const response = await apiClient.post<ExpenseCategory>('/expense-categories', data);
    return response.data;
  },

  update: async (id: string, data: ExpenseCategoryUpdate): Promise<ExpenseCategory> => {
    const response = await apiClient.put<ExpenseCategory>(`/expense-categories/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/expense-categories/${id}`);
  },
};

// ─── Trips API ───

export const tripsAPI = {
  list: async (params?: { project_id?: string; status?: string }): Promise<BusinessTrip[]> => {
    const response = await apiClient.get<BusinessTrip[]>('/trips', { params });
    return response.data;
  },

  get: async (id: string): Promise<BusinessTrip> => {
    const response = await apiClient.get<BusinessTrip>(`/trips/${id}`);
    return response.data;
  },

  create: async (data: TripCreate): Promise<BusinessTrip> => {
    const response = await apiClient.post<BusinessTrip>('/trips', data);
    return response.data;
  },

  update: async (id: string, data: TripUpdate): Promise<BusinessTrip> => {
    const response = await apiClient.put<BusinessTrip>(`/trips/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/trips/${id}`);
  },

  listExpenses: async (id: string): Promise<Expense[]> => {
    const response = await apiClient.get<Expense[]>(`/trips/${id}/expenses`);
    return response.data;
  },

  // Download an export through apiClient so the auth interceptor attaches a
  // valid token and refreshes it on 401 (a raw fetch would send a stale token
  // and save the "Could not validate credentials" JSON as the file).
  exportBlob: async (id: string, format: 'pdf' | 'csv' | 'docx'): Promise<Blob> => {
    const response = await apiClient.get(`/trips/${id}/export/${format}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// ─── Expenses API ───

export const expensesAPI = {
  list: async (params?: {
    date?: string;
    start_date?: string;
    end_date?: string;
    project_id?: string;
    client_id?: string;
    expense_category_id?: string;
    trip_id?: string;
    no_trip?: boolean;
    search?: string;
  }): Promise<Expense[]> => {
    const response = await apiClient.get<Expense[]>('/expenses', { params });
    return response.data;
  },

  get: async (id: string): Promise<Expense> => {
    const response = await apiClient.get<Expense>(`/expenses/${id}`);
    return response.data;
  },

  create: async (data: ExpenseCreate): Promise<Expense> => {
    const response = await apiClient.post<Expense>('/expenses', data);
    return response.data;
  },

  // Split one expense into several categorized expenses (e.g. hotel: lodging + meals)
  split: async (
    id: string,
    items: { amount: number; expense_category_id?: string; description?: string; date?: string }[]
  ): Promise<Expense[]> => {
    const response = await apiClient.post<Expense[]>(`/expenses/${id}/split`, { items });
    return response.data;
  },

  createBulk: async (entries: ExpenseCreate[]): Promise<BulkExpenseResult> => {
    const response = await apiClient.post<BulkExpenseResult>('/expenses/bulk', { entries });
    return response.data;
  },

  update: async (id: string, data: ExpenseUpdate): Promise<Expense> => {
    const response = await apiClient.put<Expense>(`/expenses/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/expenses/${id}`);
  },

  getSummary: async (startDate: string, endDate: string): Promise<ExpenseSummary> => {
    const response = await apiClient.get<ExpenseSummary>('/expenses/summary', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },
};

// ─── Receipts API ───

export const receiptsAPI = {
  upload: async (file: File): Promise<{ image_url: string; thumbnail_url: string; filename: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/receipts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  processOCR: async (expenseId: string): Promise<any> => {
    const response = await apiClient.post(`/receipts/${expenseId}/ocr`);
    return response.data;
  },

  // OCR an already-uploaded receipt image before an expense exists (camera-first flow)
  processOCRFile: async (imageUrl: string): Promise<any> => {
    const response = await apiClient.post('/receipts/ocr-file', { image_url: imageUrl });
    return response.data;
  },

  // Combine multiple uploaded page images into one composite receipt + OCR across all pages
  combine: async (imageUrls: string[]): Promise<{ image_url: string; thumbnail_url: string; filename: string; pages: number; ocr_data: any }> => {
    const response = await apiClient.post('/receipts/combine', { image_urls: imageUrls });
    return response.data;
  },
};

// ─── Mileage API ───

export const mileageAPI = {
  list: async (params?: { start_date?: string; end_date?: string }): Promise<MileageEntry[]> => {
    const response = await apiClient.get<MileageEntry[]>('/mileage', { params });
    return response.data;
  },

  create: async (data: MileageEntryCreate): Promise<MileageEntry> => {
    const response = await apiClient.post<MileageEntry>('/mileage', data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/mileage/${id}`);
  },

  getRate: async (): Promise<{ rate_per_mile: number; year: number }> => {
    const response = await apiClient.get('/mileage/rate');
    return response.data;
  },
};

// ─── Per Diem API ───

export const perDiemAPI = {
  getRates: async (): Promise<PerDiemRates> => {
    const response = await apiClient.get<PerDiemRates>('/per-diem/rates');
    return response.data;
  },

  list: async (): Promise<PerDiemEntry[]> => {
    const response = await apiClient.get<PerDiemEntry[]>('/per-diem');
    return response.data;
  },

  create: async (data: PerDiemEntryCreate): Promise<PerDiemEntry> => {
    const response = await apiClient.post<PerDiemEntry>('/per-diem', data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/per-diem/${id}`);
  },
};

// ─── Expense Policies API ───

export const expensePoliciesAPI = {
  list: async (): Promise<ExpensePolicy[]> => {
    const response = await apiClient.get<ExpensePolicy[]>('/expense-policies');
    return response.data;
  },

  create: async (data: ExpensePolicyCreate): Promise<ExpensePolicy> => {
    const response = await apiClient.post<ExpensePolicy>('/expense-policies', data);
    return response.data;
  },

  update: async (id: string, data: ExpensePolicyUpdate): Promise<ExpensePolicy> => {
    const response = await apiClient.put<ExpensePolicy>(`/expense-policies/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/expense-policies/${id}`);
  },

  check: async (amount: number, categoryId: string | undefined, expenseDate: string): Promise<PolicyCheckResult> => {
    const response = await apiClient.get<PolicyCheckResult>('/expense-policies/check', {
      params: {
        amount,
        expense_category_id: categoryId,
        expense_date: expenseDate,
      },
    });
    return response.data;
  },
};

// Export API
export const exportAPI = {
  csv: async (params: { start_date: string; end_date: string; client_id?: string; project_id?: string }): Promise<Blob> => {
    const response = await apiClient.get('/export/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  pdf: async (params: { start_date: string; end_date: string; client_id?: string; project_id?: string }): Promise<Blob> => {
    const response = await apiClient.get('/export/pdf', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  expensesCsv: async (params: { start_date: string; end_date: string; client_id?: string; project_id?: string }): Promise<Blob> => {
    const response = await apiClient.get('/export/expenses/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  expensesPdf: async (params: { start_date: string; end_date: string; client_id?: string; project_id?: string }): Promise<Blob> => {
    const response = await apiClient.get('/export/expenses/pdf', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  weeklySummaryPdf: async (params: { start_date: string; end_date: string; group_by: 'category' | 'subproject' | 'both'; client_id?: string; project_id?: string }): Promise<Blob> => {
    const response = await apiClient.get('/export/weekly-summary/pdf', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  weeklySummaryDocx: async (params: { start_date: string; end_date: string; group_by: 'category' | 'subproject' | 'both'; client_id?: string; project_id?: string }): Promise<Blob> => {
    const response = await apiClient.get('/export/weekly-summary/docx', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// ===== Ad Hoc Query (natural-language assistant over your data) =====

export interface AdhocMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AdhocQueryResponse {
  answer: string;
  warning?: string | null;
  context_stats: {
    time_entries: number;
    expenses: number;
    notes: number;
    truncated: boolean;
  };
  session_id: string;
  session_title: string;
}

export interface ChatSessionSummary {
  id: string;
  title: string;
  message_count: number;
  updated_at: string;
}

export interface ChatSessionDetail {
  id: string;
  title: string;
  messages: AdhocMessage[];
  client_id?: string | null;
  project_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  updated_at: string;
}

export const adhocQueryAPI = {
  ask: async (params: {
    start_date: string;
    end_date: string;
    client_id?: string;
    project_id?: string;
    messages: AdhocMessage[];
    session_id?: string;
  }): Promise<AdhocQueryResponse> => {
    const response = await apiClient.post<AdhocQueryResponse>('/adhoc-query', params);
    return response.data;
  },
  listSessions: async (): Promise<ChatSessionSummary[]> => {
    const response = await apiClient.get<ChatSessionSummary[]>('/adhoc-query/sessions');
    return response.data;
  },
  getSession: async (id: string): Promise<ChatSessionDetail> => {
    const response = await apiClient.get<ChatSessionDetail>(`/adhoc-query/sessions/${id}`);
    return response.data;
  },
  renameSession: async (id: string, title: string): Promise<ChatSessionDetail> => {
    const response = await apiClient.patch<ChatSessionDetail>(`/adhoc-query/sessions/${id}`, { title });
    return response.data;
  },
  deleteSession: async (id: string): Promise<void> => {
    await apiClient.delete(`/adhoc-query/sessions/${id}`);
  },
};

// ===== Task Types =====

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'in_review' | 'done' | 'cancelled';
export type TaskPriority = 'urgent' | 'high' | 'medium' | 'low' | 'none';

export interface TaskItem {
  id: string;
  user_id: string;
  project_id: string;
  parent_task_id?: string | null;
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  category_id?: string | null;
  assigned_to?: string | null;
  subproject_id?: string | null;
  subproject_name?: string | null;
  sort_order: number;
  estimated_hours?: number | null;
  actual_hours?: number | null;
  actual_hours_override: boolean;
  estimated_hours_override: boolean;
  date_created: string;
  date_due?: string | null;
  dates_worked?: string[];
  date_completed?: string | null;
  tags?: string[];
  created_at: string;
  updated_at?: string | null;
  subtask_count: number;
  subtask_done_count: number;
  subtask_estimated_hours_total?: number | null;
  subtask_actual_hours_total?: number | null;
  attachment_count: number;
  time_entry_count: number;
  project_name?: string | null;
  client_name?: string | null;
  client_id?: string | null;
  category_name?: string | null;
  category_color?: string | null;
}

export interface TaskWithSubtasks extends TaskItem {
  subtasks: TaskItem[];
}

export interface TaskCreate {
  project_id: string;
  parent_task_id?: string | null;
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  category_id?: string | null;
  assigned_to?: string;
  subproject_id?: string | null;
  estimated_hours?: number | null;
  date_due?: string | null;
  tags?: string[];
}

export interface TaskUpdate {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  category_id?: string | null;
  assigned_to?: string | null;
  subproject_id?: string | null;
  sort_order?: number;
  estimated_hours?: number | null;
  actual_hours?: number | null;
  actual_hours_override?: boolean;
  estimated_hours_override?: boolean;
  date_due?: string | null;
  tags?: string[];
}

export interface TaskSummary {
  project_id: string;
  total: number;
  by_status: Record<string, number>;
}

export interface TaskAttachment {
  id: string;
  task_id: string;
  user_id: string;
  original_filename: string;
  stored_filename: string;
  file_size: number;
  mime_type: string;
  created_at: string;
  download_url: string;
}

export interface TaskLinkItem {
  id: string;
  task_id: string;
  user_id: string;
  url: string;
  title?: string | null;
  created_at: string;
}

export interface TaskLinkCreate {
  url: string;
  title?: string;
}

export interface TaskTimeEntryLink {
  link_id: string;
  time_entry_id: string;
  date: string;
  description: string;
  hours: number;
  category: string;
  created_at?: string;
}

// ===== Task API =====

export const tasksAPI = {
  list: async (params?: {
    project_id?: string;
    status?: string;
    priority?: string;
    parent_task_id?: string;
    search?: string;
    date_due_before?: string;
    date_due_after?: string;
  }): Promise<TaskItem[]> => {
    const response = await apiClient.get('/tasks', { params });
    return response.data;
  },

  get: async (taskId: string): Promise<TaskWithSubtasks> => {
    const response = await apiClient.get(`/tasks/${taskId}`);
    return response.data;
  },

  create: async (data: TaskCreate): Promise<TaskItem> => {
    const response = await apiClient.post('/tasks', data);
    return response.data;
  },

  update: async (taskId: string, data: TaskUpdate): Promise<TaskItem> => {
    const response = await apiClient.put(`/tasks/${taskId}`, data);
    return response.data;
  },

  delete: async (taskId: string): Promise<void> => {
    await apiClient.delete(`/tasks/${taskId}`);
  },

  updateStatus: async (taskId: string, status: TaskStatus): Promise<TaskItem> => {
    const response = await apiClient.patch(`/tasks/${taskId}/status`, { status });
    return response.data;
  },

  reorder: async (taskId: string, sortOrder: number): Promise<TaskItem> => {
    const response = await apiClient.patch(`/tasks/${taskId}/reorder`, { sort_order: sortOrder });
    return response.data;
  },

  batchReorder: async (taskIds: string[]): Promise<TaskItem[]> => {
    const response = await apiClient.patch('/tasks/batch-reorder', { task_ids: taskIds });
    return response.data;
  },

  duplicate: async (taskId: string, includeSubtasks: boolean = false): Promise<TaskItem> => {
    const response = await apiClient.post(`/tasks/${taskId}/duplicate`, null, {
      params: { include_subtasks: includeSubtasks },
    });
    return response.data;
  },

  summary: async (projectId: string): Promise<TaskSummary> => {
    const response = await apiClient.get('/tasks/summary', { params: { project_id: projectId } });
    return response.data;
  },
};

// ===== Task Attachment API =====

export const taskAttachmentsAPI = {
  list: async (taskId: string): Promise<TaskAttachment[]> => {
    const response = await apiClient.get(`/tasks/${taskId}/attachments`);
    return response.data;
  },

  upload: async (taskId: string, file: File): Promise<TaskAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  download: async (attachmentId: string): Promise<Blob> => {
    const response = await apiClient.get(`/task-attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  delete: async (attachmentId: string): Promise<void> => {
    await apiClient.delete(`/task-attachments/${attachmentId}`);
  },
};

// ===== Task Time Entry API =====

export const taskTimeEntriesAPI = {
  list: async (taskId: string): Promise<TaskTimeEntryLink[]> => {
    const response = await apiClient.get(`/tasks/${taskId}/time-entries`);
    return response.data;
  },

  link: async (taskId: string, timeEntryId: string): Promise<any> => {
    const response = await apiClient.post(`/tasks/${taskId}/time-entries`, {
      time_entry_id: timeEntryId,
    });
    return response.data;
  },

  unlink: async (taskId: string, timeEntryId: string): Promise<void> => {
    await apiClient.delete(`/tasks/${taskId}/time-entries/${timeEntryId}`);
  },

  logTime: async (taskId: string, data: {
    date: string;
    description: string;
    hours: number;
    category?: string;
    subproject_id?: string;
    start_time?: string;
  }): Promise<any> => {
    const response = await apiClient.post(`/tasks/${taskId}/log-time`, data);
    return response.data;
  },
};

export const taskLinksAPI = {
  list: async (taskId: string): Promise<TaskLinkItem[]> => {
    const response = await apiClient.get(`/tasks/${taskId}/links`);
    return response.data;
  },

  create: async (taskId: string, data: TaskLinkCreate): Promise<TaskLinkItem> => {
    const response = await apiClient.post(`/tasks/${taskId}/links`, data);
    return response.data;
  },

  delete: async (linkId: string): Promise<void> => {
    await apiClient.delete(`/task-links/${linkId}`);
  },
};

// ─── Notes Search API ───

export interface NoteSearchMatch {
  attachment_id: string; // carries the DailyNote id when source === 'other'
  source: 'meeting' | 'other';
  original_filename: string;
  mime_type: string;
  file_size: number;
  snippet: string;
  date: string;
  description: string;
  // Time-entry context — present only when source === 'meeting'
  time_entry_id: string | null;
  hours: number | null;
  category: string | null;
  subproject_id: string | null;
  subproject_name: string | null;
  project_id: string | null;
  project_name: string | null;
  client_id: string | null;
  client_name: string | null;
}

export const searchAPI = {
  notes: async (params: {
    q: string;
    project_id?: string;
    start_date?: string;
    end_date?: string;
    category?: string;
    subproject_id?: string;
  }): Promise<NoteSearchMatch[]> => {
    const response = await apiClient.get<NoteSearchMatch[]>('/search/notes', { params });
    return response.data;
  },
};
